import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { API_BASE_URL } from '../../../config';
import { legendTooltipFormatter } from './legendTooltip';

interface PowerPoint {
  duration_seconds: number;
  power_watts: number;
  watts_per_kg: number;
}

interface MergedPoint {
  duration_seconds: number;
  label: string;
  all_time: number | null;
  recent: number | null;
}

type ChartRange = '30d' | '90d' | '6mo' | '1yr' | 'all';

const rangeOptions: { value: ChartRange; label: string }[] = [
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '6mo', label: '6mo' },
  { value: '1yr', label: '1yr' },
  { value: 'all', label: 'All' },
];

const durationLabel = (secs: number): string => {
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.round(secs / 60)}min`;
  return `${(secs / 3600).toFixed(0)}hr`;
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0]?.payload;
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4, color: '#333' }}>{point?.label || label}</div>
      {payload.map((p: any) => (
        <div key={p.name} style={{ color: p.stroke || '#333', marginBottom: 2 }}>
          {p.name}: {p.value !== null ? `${Math.round(p.value)}W` : '--'}
        </div>
      ))}
    </div>
  );
};

interface PowerCurveChartProps {
  targetUserId?: number | null;
}

const PowerCurveChart: React.FC<PowerCurveChartProps> = ({ targetUserId }) => {
  const [data, setData] = useState<MergedPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<ChartRange>('all');
  const [rangeLabel, setRangeLabel] = useState<string>('Last 6 weeks');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ range });
      if (targetUserId) params.set('target_user_id', String(targetUserId));
      const res = await fetch(`${API_BASE_URL}/api/analytics/power-curve?${params}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load power curve');
      const json = await res.json();

      const allTime: PowerPoint[] = json.all_time || [];
      const recent: PowerPoint[] = json.recent || json.six_weeks || [];
      const label: string = json.range_label || 'Last 6 weeks';
      setRangeLabel(label);

      const recentMap = new Map<number, number>();
      recent.forEach((p) => recentMap.set(p.duration_seconds, p.power_watts));

      const merged: MergedPoint[] = allTime.map((p) => ({
        duration_seconds: p.duration_seconds,
        label: durationLabel(p.duration_seconds),
        all_time: p.power_watts,
        recent: recentMap.get(p.duration_seconds) ?? null,
      }));

      setData(merged);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [range, targetUserId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="analytics-card">
      <h3>Power Curve</h3>
      <div className="analytics-range-buttons">
        {rangeOptions.map((opt) => (
          <button
            key={opt.value}
            className={`analytics-range-btn ${range === opt.value ? 'active' : ''}`}
            onClick={() => setRange(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="analytics-chart-loading">Loading...</div>
      ) : data.length === 0 ? (
        <div className="analytics-chart-empty">No power curve data available</div>
      ) : (
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 12, fill: '#999' }}
              interval="preserveStartEnd"
            />
            <YAxis tick={{ fontSize: 12, fill: '#999' }} />
            <Tooltip content={<CustomTooltip />} isAnimationActive={false} />
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              iconType="line"
              formatter={legendTooltipFormatter({
                'All-time': 'Your best power output ever recorded at each duration.',
                [rangeLabel]: 'Your best power at each duration from the last 6 weeks. Compare with all-time to see current form.',
              })}
            />
            <Line
              type="monotone"
              dataKey="all_time"
              stroke="#7c3aed"
              strokeWidth={2}
              dot={false}
              name="All-time"
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="recent"
              stroke="#f59e0b"
              strokeWidth={1.5}
              strokeDasharray="5 3"
              dot={false}
              name={rangeLabel}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default PowerCurveChart;
